import React, { RefObject } from 'react';
import { injectV2 } from '../../../injectV2';
import { CategoriesStore } from '../../../stores/CategoriesStore';
import { ConfigStore } from '../../../stores/ConfigStore';
import { isFiltersEmpty, RoutingStore } from '../../../stores/RoutingStore';
import { TemplatesStore } from '../../../stores/TemplatesStore';
import { getActiveCategorySlug, getActiveSubCategorySlug } from '../../../stores/routes/route';
import { WithTranslations, withTranslations } from '../../../../utils/withTranslations';
import s from './SubTitle.scss';
import { subTitleDataHooks } from './SubTitle.dataHooks';

interface SubTitleProps extends WithTranslations {
  configStore: ConfigStore;
  routingStore: RoutingStore;
  categoriesStore: CategoriesStore;
  templatesStore: TemplatesStore;
  subTitleRef?: RefObject<HTMLHeadingElement>;
  dataHook?: string;
}

const SubTitleCmp: React.FC<SubTitleProps> = (props) => {
  const { configStore, templatesStore, t, dataHook, categoriesStore, routingStore, subTitleRef } = props;
  const { primaryCategorySlug } = routingStore;

  const getTitle = (): string => {
    const activeSubcategorySlug = getActiveSubCategorySlug(routingStore.matchedRoute);
    const activeSubcategory = categoriesStore.getCategoryBySlug(activeSubcategorySlug);

    if (activeSubcategory) {
      return t('subHeader.subTitle.category', { category: configStore.getCategoryTitle(activeSubcategory, t) });
    }

    const activeCategorySlug = getActiveCategorySlug(routingStore.matchedRoute, primaryCategorySlug);
    const activeCategory = categoriesStore.getCategoryBySlug(activeCategorySlug);

    if (activeCategory) {
      return t('subHeader.subTitle.category', { category: configStore.getCategoryTitle(activeCategory, t) });
    }

    if (routingStore.matchedRoute.routeName === 'search') {
      if (templatesStore.loadedFetchParams?.type === 'search') {
        const searchTextKey =
          isFiltersEmpty(templatesStore.loadedFetchParams.filters) && templatesStore.isNoResultSuggestions
            ? 'subHeader.subTitle.noResults'
            : 'subHeader.subTitle.search';
        return t(searchTextKey, { query: templatesStore.loadedFetchParams.criteria });
      }

      const loadedSubCategorySlug =
        templatesStore.loadedFetchParams?.type === 'category'
          ? templatesStore.loadedFetchParams?.subCategorySlug
          : null;
      const loadedSubCategory = categoriesStore.getCategoryBySlug(loadedSubCategorySlug);

      if (loadedSubCategory) {
        return t('subHeader.subTitle.category', { category: configStore.getCategoryTitle(loadedSubCategory, t) });
      }

      const loadedCategorySlug =
        templatesStore.loadedFetchParams?.type === 'category' ? templatesStore.loadedFetchParams?.categorySlug : null;
      const loadedCategory = categoriesStore.getCategoryBySlug(loadedCategorySlug);

      if (loadedCategory) {
        return t('subHeader.subTitle.category', { category: configStore.getCategoryTitle(loadedCategory, t) });
      }
    }

    return t('subHeader.subTitle.allTemplates');
  };

  const TitleTagName = props.routingStore.matchedRoute.routeName === 'home' ? 'h2' : 'h1';

  return (
    <div ref={subTitleRef} tabIndex={-1} className={s.root} data-hook={dataHook}>
      <TitleTagName
        className={s.heading}
        data-hook={subTitleDataHooks.title()}
        aria-live="assertive"
        aria-atomic="true"
      >
        {getTitle()}
      </TitleTagName>
    </div>
  );
};

export const SubTitle = withTranslations(
  injectV2('routingStore', 'categoriesStore', 'templatesStore', 'configStore')(SubTitleCmp),
);
